/**
 * Fly Fit Finder wizard — vanilla JS, framework-free.
 *
 * Designed to mount inside a Shopify theme section (Online Store 2.0) but works
 * anywhere with a root element. Public API:
 *
 *   FlyFitFinder.mount(rootEl, {
 *     rulesUrl,                 // required, URL to fly-fit-finder-rules.json
 *     productMapUrl,            // optional, URL to product-handle-map.json
 *     imagesBase,               // optional, URL prefix for question images
 *     productUrlBase,           // optional, base for /products/<handle>
 *     fallbackSearchUrl,        // optional, base for fallback search (default '/search?q=')
 *     onComplete,               // optional, callback(result) at result render
 *   });
 *
 * Analytics: pushes events to window.dataLayer when present, also calls gtag
 * directly when defined. No-ops otherwise.
 */
(function (root) {
  "use strict";

  var DEFAULTS = {
    productUrlBase: "/products/",
    fallbackSearchUrl: "/search?q=",
    imagesBase: "",
    /** Optional explicit map of image basename -> URL. Takes precedence over imagesBase.
     * Used when the host (e.g. Shopify) version-busts each asset separately. */
    imageUrls: null,
    /** Collection link used for "browse all" CTAs and unmatched fallbacks.
     * Default targets Schneider's horse-fly-sheets collection on sstack.com. */
    collectionUrl: "/collections/horse-fly-sheets",
    /** Optional URL to a fit-specialist contact page; if blank, CTA is hidden. */
    fitSpecialistUrl: "",
    /** Optional measuring-guide URL surfaced in 'How to measure' helpers. */
    measuringGuideUrl: "",
    /** Klaviyo public key (e.g. 'PXXXXX'). When set, completion + email-me events are forwarded to Klaviyo. */
    klaviyoPublicKey: "",
    /** Optional Klaviyo list ID used by the 'email me my results' opt-in. */
    klaviyoListId: "",
    /** Optional canonical URL for share-back links. When set, share URLs use
     *  this base instead of the page the shopper happened to land on. Set this
     *  to the dedicated Fly Fit Finder page (e.g. https://www.sstack.com/pages/fly-fit-finder)
     *  so a shopper who completes the wizard on /pages/about-us doesn't share
     *  a link that lands recipients on /pages/about-us. */
    shareUrlBase: "",
    /** Storage key prefix; final key is suffixed with version + customer scope. */
    storageKey: "flyFitFinder",
    /** Storage version — bump to invalidate stale saved sessions. */
    storageVersion: 2,
    /** Logged-in customer id, used to scope localStorage so multiple shoppers on one device don't collide. */
    customerId: null,
    /** Toggle features (mostly for tests). */
    enablePersistence: true,
    enableAbandonTracking: true,
    enableShareLinks: true,
    enableHorseProfiles: true,
    enableEmailCapture: true
  };

  function imageUrl(opts, basename) {
    if (!basename) return null;
    if (opts && opts.imageUrls && opts.imageUrls[basename]) return opts.imageUrls[basename];
    return (opts && opts.imagesBase ? opts.imagesBase : "") + basename;
  }

  // --------------------------------------------------------------------------
  // Step definitions — each step describes a single question.
  // `optional: true` lets users skip without blocking results.
  // --------------------------------------------------------------------------
  var STEPS = [
    {
      id: "horse_name",
      type: "text",
      label: "What is your horse's name?",
      help: "We'll use this to personalize your recommendation. Optional.",
      optional: true,
      placeholder: "e.g. Fluffy"
    },
    {
      id: "zip",
      type: "zip",
      label: "What is your zip code?",
      help: "We use this to figure out your typical fly-season climate (wet vs dry).",
      placeholder: "e.g. 44087"
    },
    {
      id: "horse_type",
      type: "image-radio",
      label: "Is your horse a...",
      help: "Pick the closest match. (We currently don't carry foal-sized fly sheets.)",
      options: [
        { value: "Mini",  label: "Mini",            image: "Mini.jpg" },
        { value: "Pony",  label: "Pony",            image: "Pony.jpg" },
        { value: "Horse", label: "Horse",           image: "horse.jpg" },
        { value: "Draft", label: "Draft",           image: "Draft.jpg" }
      ]
    },
    {
      id: "breed",
      type: "select",
      label: "What breed best describes your horse?",
      help: "Used to translate height into a fly-sheet size.",
      options: ["Stock", "Warmblood", "Thoroughbred", "Arabian", "Pony", "Mini", "Draft"]
    },
    {
      id: "height",
      type: "select",
      label: "How tall is your horse?",
      help: "Hands for Horse / Pony / Draft. Inches for Mini.",
      // Options computed at render time based on horse_type.
      dynamic: "height_options"
    },
    {
      id: "chest",
      type: "image-radio",
      label: "Which best matches your horse's chest?",
      measure: "Stand in front of your horse and look at how their chest fills the space between their front legs. Wide = full, muscular barrel; Standard = average; Narrow = lean / refined.",
      options: [
        { value: "Narrow",   label: "Narrow",   image: "Narrow Chest Width.jpg" },
        { value: "Standard", label: "Standard", image: "Medium Chest Width.jpg" },
        { value: "Wide",     label: "Wide",     image: "WIDE Chest Width.jpg" }
      ]
    },
    {
      id: "withers_and_head_carriage",
      type: "image-radio",
      label: "Which best matches your horse's withers & head carriage?",
      measure: "Withers = the bony ridge at the base of the neck. Head carriage = where they naturally hold their head when relaxed. High wither + high head = warmblood / TB type; low + low = stock / draft type.",
      options: [
        { value: "Low/Low",   label: "Low withers, low head carriage",   image: "Low Wither Low Head Carriage.jpg" },
        { value: "Low/High",  label: "Low withers, high head carriage",  image: "Low WitherHigh Head Carriage.jpg" },
        { value: "High/Low",  label: "High withers, low head carriage",  image: "HighWitherLowHeadCarriage.jpg" },
        { value: "High/High", label: "High withers, high head carriage", image: "HighWitherHighHeadCarriage.jpg" }
      ]
    },
    {
      id: "shoulders",
      type: "image-radio",
      label: "Which best matches your horse's shoulders?",
      measure: "Look at the angle from the point of shoulder up to the wither. A more horizontal angle = sloped (think TB/warmblood); a steeper, more vertical angle = upright (think stock/draft).",
      options: [
        { value: "Sloped",  label: "Sloped",  image: "Sloped-Shoulders.jpg" },
        { value: "Upright", label: "Upright", image: "Upright-Shoulders.jpg" }
      ]
    },
    {
      id: "mane",
      type: "image-radio",
      label: "Which best matches your horse's mane?",
      measure: "If the mane regularly hangs past the crest of the neck, choose Long. If it's pulled, roached, or naturally short and stays close to the neck, choose Short / Pulled.",
      options: [
        { value: "Long",  label: "Long",   image: "LongMane.jpg" },
        { value: "Short", label: "Short / Pulled", image: "PulledMane.jpg" }
      ]
    },
    {
      id: "horse_age",
      type: "select",
      label: "How old is your horse?",
      help: "We bucket into 'Old' (15+) and 'Not Old' (under 15) for fit purposes.",
      // Numeric values matching Kalio's canonical horse_age dropdown (0..31). Boundary
      // labels: 0 = "Under 1 year", 31 = "Over 30 years". Source: 200720 admin export.
      options: (function () {
        var arr = [];
        for (var i = 0; i <= 31; i++) {
          var label = i === 0 ? "Under 1 year"
                    : i === 1 ? "1 year"
                    : i === 31 ? "Over 30 years"
                    : (i + " years");
          arr.push({ value: String(i), label: label });
        }
        return arr;
      })()
    },
    {
      id: "turnout",
      type: "image-radio",
      label: "When is your horse turned out during fly season?",
      options: [
        { value: "Day",   label: "Day only",    image: "HorseOutDay.jpg" },
        { value: "Night", label: "Night only",  image: "HorseOutNight.jpg" },
        { value: "24/7",  label: "Day & Night", image: "HorseOut_24-7.jpg" }
      ]
    },
    {
      id: "temperament",
      type: "image-radio",
      label: "What is your horse's temperament & buddy situation?",
      options: [
        { value: "Calm/Alone",  label: "Calm, lives alone",       image: "Activity_CalmLivesAlone.jpg" },
        { value: "Calm/Calm",   label: "Calm with calm buddies",   image: "Activity_CalmwithCalmBuddies.jpg" },
        { value: "Calm/Rough",  label: "Calm with rough buddies",  image: "Activity_CalmRoughBuddies.jpg" },
        { value: "Rough/Calm",  label: "Rough with calm buddies",  image: "Activity_RoughWithCalmBuddies.jpg" },
        { value: "Rough/Rough", label: "Rough with rough buddies", image: "Activity_RoughWithRoughBuddies.jpg" }
      ]
    },
    {
      id: "worst_reaction",
      type: "image-radio",
      label: "Which insects bother your horse the most?",
      options: [
        { value: "Gnats and Mosquitos",     label: "Gnats & mosquitos",       image: "Gnats-Mosquitos Insects.jpg" },
        { value: "Stable and Deer Flies",   label: "Stable & deer flies",     image: "Stable DeerFlies Insects.jpg" },
        { value: "B-52 Bomber (Horse flies)", label: "B-52 bombers (horseflies)", image: "B-52 Insects.jpg" },
        { value: "All",                     label: "All of the above",        image: "ALL Insects.jpg" }
      ]
    },
    {
      id: "sensitivity",
      type: "image-radio",
      label: "How sensitive is your horse to flies?",
      help: "Sensitivity drives how much coverage we recommend.",
      options: [
        { value: "Calm",   label: "Calm — barely reacts",          image: "Calm_Reaction.jpg" },
        { value: "Medium", label: "Medium — typical reaction",     image: "Medium_Reaction.jpg" },
        { value: "High",   label: "High — needs maximum coverage", image: "High_Reaction.jpg" }
      ]
    },
    // Last step — soft email gate. The user can either submit their email
    // (Klaviyo identify + "Fly Finder Email Capture" event fires, then we
    // advance to the result page) OR click "No thanks, just show me the
    // recommendations" to skip straight to results without firing Klaviyo.
    // optional:true keeps canAdvance() permissive so the standard nav doesn't
    // block — the actual primary/skip behavior is wired by renderEmailCaptureStep.
    {
      id: "email_capture",
      type: "email-capture",
      optional: true,
      label: "Want a copy of your recommendations?",
      help: "We'll email you a personalized summary with the products we recommend, your horse's profile, and a link you can revisit anytime."
    }
  ];

  // Heights per horse_type bucket. Mini uses inches; everyone else hands.
  // Foal is intentionally absent — Schneider does not stock foal-sized fly sheets.
  // Horse extends down to 13 hands (covers small Arabians under 14h — Caitlin
  // 2026-05-26 finding: a 13-hand Arabian owner won't pick "Pony" naturally,
  // so let them stay in the Horse track and let size_rule route them to the
  // right fly-sheet size) and up to 19 hands; Draft covers 15.3-21 hands.
  // New (breed, height) rows in size_rule_table.tsv are extrapolated from the
  // existing per-breed cadence — see FOR_MERCH_REVIEW §10 for merch sign-off.
  var HEIGHT_OPTIONS_BY_TYPE = {
    Mini: ["29 inches", "30 inches", "31 inches", "32 inches", "33 inches", "34 inches", "35 inches", "36 inches", "37 inches", "38 inches", "39 inches"],
    Pony: ["10 hands", "10.2 hands", "10.3 hands", "11 hands", "11.2 hands", "11.3 hands", "12 hands", "12.1 hands", "12.2 hands", "12.3 hands", "13 hands", "13.1 hands", "13.2 hands", "13.3 hands", "14 hands", "14.1 hands", "14.2 hands"],
    Horse: ["13 hands", "13.1 hands", "13.2 hands", "13.3 hands", "14 hands", "14.1 hands", "14.2 hands", "14.3 hands", "15 hands", "15.1 hands", "15.2 hands", "15.3 hands", "16 hands", "16.1 hands", "16.2 hands", "16.3 hands", "17 hands", "17.1 hands", "17.2 hands", "17.3 hands", "18 hands", "18.1 hands", "18.2 hands", "18.3 hands", "19 hands"],
    Draft: ["15.3 hands", "16 hands", "16.1 hands", "16.2 hands", "16.3 hands", "17 hands", "17.1 hands", "17.2 hands", "17.3 hands", "18 hands", "18.1 hands", "18.2 hands", "18.3 hands", "19 hands", "19.1 hands", "19.2 hands", "19.3 hands", "20 hands", "20.1 hands", "20.2 hands", "20.3 hands", "21 hands"]
  };

  // ----------------------------------------------------------------------------
  // Narrative copy map — value_sentence_display from Kalio's FlyFinderAnswerValues
  // table (HORS - Fly Sheet Finder table data - 7-30-20.xlsx). Used to render
  // the result-page summary in Schneider's historical brand voice and to populate
  // the analytics payload + email recap with consistent wording.
  // ----------------------------------------------------------------------------
  var NARRATIVE = {
    breed: {
      "Stock": "Stock Horse breed", "Warmblood": "Warmblood", "Thoroughbred": "Thoroughbred",
      "Arabian": "Arabian", "Pony": "Pony", "Mini": "Mini", "Draft": "Draft"
    },
    horse_type: { "Mini": "Mini", "Pony": "Pony", "Horse": "Horse", "Draft": "Draft" },
    chest: { "Wide": "wide", "Standard": "standard", "Narrow": "narrow" },
    mane: { "Long": "long mane", "Short": "short mane" },
    sensitivity: { "Calm": "calm", "Medium": "medium", "High": "highly sensitive" },
    shoulders: { "Sloped": "sloped shoulders", "Upright": "upright shoulders" },
    temperament: {
      "Calm/Alone":  "calm and lives alone",
      "Calm/Calm":   "calm with calm buddies",
      "Calm/Rough":  "calm with rowdy buddies",
      "Rough/Calm":  "rowdy with calm buddies",
      "Rough/Rough": "rowdy with rowdy buddies"
    },
    turnout: { "Day": "day only", "Night": "night only", "24/7": "around the clock" },
    withers_and_head_carriage: {
      "Low/Low":   "low wither, low head carriage",
      "High/Low":  "high wither, low head carriage",
      "Low/High":  "low wither, high head carriage",
      "High/High": "high wither, high head carriage"
    },
    worst_reaction: {
      "Gnats and Mosquitos": "gnats and mosquitos",
      "Stable and Deer Flies": "stable and deer flies",
      "B-52 Bomber (Horse flies)": "B-52 bombers (horseflies)",
      "All": "all types of flies"
    }
  };

  function narrate(field, value) {
    if (value == null || value === "") return "";
    if (NARRATIVE[field] && NARRATIVE[field][value]) return NARRATIVE[field][value];
    return String(value);
  }

  function ageNarrative(value) {
    if (value === "" || value == null) return "";
    var n = parseInt(value, 10);
    if (isNaN(n)) return "";
    if (n === 0) return "under 1 year old";
    if (n === 1) return "1 year old";
    if (n === 31) return "over 30 years old";
    return n + " years old";
  }

  // ----------------------------------------------------------------------------
  // Plain-English horse summary used by Klaviyo (recap email body) and as a
  // fallback subtitle in the result UI. Pattern requested by retention:
  //   "Gunner is a 20 year old Quarter Horse who lives in PA with rough pasturemates."
  // The state is the resolved 2-letter code (from zip) — we keep it short
  // because the email already has more verbose climate/fit/fabric copy below.
  // ----------------------------------------------------------------------------
  function buildHorseSummary(answers, derived) {
    if (!answers) return "";
    var name = answers.horse_name && String(answers.horse_name).trim();
    if (!name) name = "This horse";

    // Adjectival "a 20 year old" form (singular even for >1 year — that's
    // English when the phrase modifies a noun). Boundary cases mirror the
    // wizard's age-step labels.
    var ageRaw = parseInt(answers.horse_age, 10);
    var ageClause = "";
    if (!isNaN(ageRaw)) {
      if (ageRaw === 0) ageClause = "a foal";
      else if (ageRaw === 31) ageClause = "a senior";
      else if (ageRaw === 1) ageClause = "a 1 year old";
      else if (ageRaw >= 2 && ageRaw <= 30) ageClause = "a " + ageRaw + " year old";
    }

    // Breed comes from the canonical NARRATIVE map (e.g. "Stock Horse breed",
    // "Warmblood"). Strip the trailing " breed" suffix because it reads
    // awkwardly inside this sentence.
    var breedWord = narrate("breed", answers.breed) || narrate("horse_type", answers.horse_type) || "horse";
    breedWord = String(breedWord).replace(/\s+breed$/i, "").trim();

    var stateWord = (derived && derived.state) ? derived.state : "your area";
    var temperWord = narrate("temperament", answers.temperament);

    var pieces = [name, "is"];
    if (ageClause) pieces.push(ageClause);
    else pieces.push("a"); // "Gunner is a Warmblood who…" when age is unknown
    if (breedWord) pieces.push(breedWord);
    pieces.push("who lives in", stateWord);
    if (temperWord) pieces.push("with", temperWord);
    return pieces.join(" ").replace(/\s+/g, " ").trim() + ".";
  }

  // ----------------------------------------------------------------------------
  // Soft sanity-warning rules — surfaced when breed and horse_type appear to
  // disagree. Open questions originally raised by the Kalio team in 2020 and
  // never resolved in product (e.g. Quarter Pony, small Arabian). We do not
  // block the user; we just flag and let them confirm or adjust.
  // ----------------------------------------------------------------------------
  function breedTypeWarning(answers) {
    var t = answers.horse_type;
    var b = normalizeBreed(answers.breed);
    var h = answers.height || "";
    var heightHands = parseFloat(h);
    if (!t || !b) return null;
    if (t === "Pony" && b === "Stock" && heightHands >= 13.3) {
      return "Most Stock-breed horses around " + h + " sit in our Horse fit pipeline. If your horse is a Quarter Pony, sticking with Pony is fine — otherwise try Horse for a closer match.";
    }
    if (t === "Pony" && b === "Arabian" && heightHands && heightHands < 14) {
      return "Most Arabians under 14 hands still fit in our Horse pipeline. If you've measured carefully and Pony is right, leave it; otherwise try Horse.";
    }
    if (t === "Horse" && b === "Warmblood" && heightHands >= 17) {
      return "Big Warmbloods (17h+) usually need our Draft fit. We'll still resolve a recommendation as Horse, but Draft is worth a try if the result feels tight in the shoulders.";
    }
    if (t === "Horse" && b === "Draft" && heightHands && heightHands < 16) {
      return "Most Drafts under 16 hands still fit our Horse pipeline well. Leave as Horse unless your horse is unusually heavy-bodied.";
    }
    return null;
  }

  // Slot ordering for the result card list.
  var RESULT_SLOTS = [
    { key: "sheet_1",    title: "Recommended fly sheet", primary: true },
    { key: "sheet_2",    title: "Alternate fly sheet" },
    { key: "neck_cover", title: "Matching neck cover" },
    { key: "mask",       title: "Recommended fly mask" },
    { key: "boots",      title: "Recommended fly boots" }
  ];

  // --------------------------------------------------------------------------
  // Utility helpers
  // --------------------------------------------------------------------------
  function emit(eventName, payload) {
    try {
      if (root.dataLayer && typeof root.dataLayer.push === "function") {
        root.dataLayer.push(Object.assign({ event: eventName }, payload || {}));
      }
      if (typeof root.gtag === "function") {
        root.gtag("event", eventName, payload || {});
      }
    } catch (err) {
      // Swallow analytics errors so they never break the wizard.
      if (root.console) root.console.warn("[FlyFitFinder] analytics error", err);
    }
  }

  function fetchJSON(url) {
    return fetch(url, { credentials: "same-origin" }).then(function (res) {
      if (!res.ok) throw new Error("Failed to load " + url + " (" + res.status + ")");
      return res.json();
    });
  }

  function expandColumnar(table) {
    if (!table || !table.columns || !table.rows) return [];
    var cols = table.columns;
    return table.rows.map(function (row) {
      var obj = {};
      for (var i = 0; i < cols.length; i++) obj[cols[i]] = row[i];
      return obj;
    });
  }

  function ageBucket(age) {
    var n = parseInt(age, 10);
    if (isNaN(n)) return "Not Old";
    return n >= 15 ? "Old" : "Not Old";
  }

  // Some surveys use "Stock Breed" / "Arabian-Type Breed" etc. Coerce to the
  // values the rule tables use ("Stock", "Arabian", etc.).
  function normalizeBreed(value) {
    if (!value) return value;
    return String(value).replace(/\s*Breed$/i, "").replace(/-Type$/i, "").trim();
  }

  function findInTable(rows, predicateFields, query) {
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var hit = true;
      for (var f = 0; f < predicateFields.length; f++) {
        var field = predicateFields[f];
        if (String(row[field]) !== String(query[field])) { hit = false; break; }
      }
      if (hit) return row;
    }
    return null;
  }

  // Closest-height fallback for the size_rule cascade. Used when the exact
  // (breed, height) combo isn't in the table — e.g. Horse type + Pony breed
  // at 16 hands. Match must keep the same UNIT (hands vs inches) so a
  // Mini-breed shopper who somehow has a "16 hands" answer doesn't snap
  // to "32 inches" via a numeric proximity check.
  function findClosestSizeRow(rows, breed, height) {
    if (!breed || !height) return null;
    var heightStr = String(height);
    var heightNum = parseFloat(heightStr);
    if (isNaN(heightNum)) return null;
    var heightUnit = heightStr.indexOf("inches") !== -1 ? "inches" : "hands";
    var best = null;
    var bestDist = Infinity;
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (String(row.breed) !== String(breed)) continue;
      var rowStr = String(row.height || "");
      var rowUnit = rowStr.indexOf("inches") !== -1 ? "inches" : "hands";
      if (rowUnit !== heightUnit) continue;
      var rowNum = parseFloat(rowStr);
      if (isNaN(rowNum)) continue;
      var dist = Math.abs(rowNum - heightNum);
      if (dist < bestDist) { bestDist = dist; best = row; }
    }
    return best;
  }

  // --------------------------------------------------------------------------
  // localStorage helpers — versioned + customer-scoped so multiple shoppers
  // sharing a device don't collide. All writes are wrapped in try/catch since
  // private mode / quota errors must never break the wizard.
  // --------------------------------------------------------------------------
  function storageScope(opts) {
    return opts.customerId ? ("c" + opts.customerId) : "anon";
  }
  function sessionKey(opts) {
    return opts.storageKey + ":session:v" + opts.storageVersion + ":" + storageScope(opts);
  }
  function profileKey(opts) {
    return opts.storageKey + ":profiles:v" + opts.storageVersion + ":" + storageScope(opts);
  }
  function safeRead(key) {
    try {
      if (typeof root.localStorage === "undefined") return null;
      var raw = root.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  function safeWrite(key, value) {
    try {
      if (typeof root.localStorage === "undefined") return;
      root.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) { /* swallow quota / private-mode errors */ }
  }
  function safeRemove(key) {
    try {
      if (typeof root.localStorage === "undefined") return;
      root.localStorage.removeItem(key);
    } catch (e) {}
  }

  function loadSession(opts) {
    if (!opts.enablePersistence) return null;
    return safeRead(sessionKey(opts));
  }
  function saveSession(opts, state) {
    if (!opts.enablePersistence) return;
    safeWrite(sessionKey(opts), {
      stepIndex: state.stepIndex,
      answers: state.answers,
      savedAt: Date.now()
    });
  }
  function clearSession(opts) {
    safeRemove(sessionKey(opts));
  }

  // Profile shape: { id, name, savedAt, answers }. Identified by id (the saved
  // horse_name + timestamp). Listed newest-first when read.
  function loadProfiles(opts) {
    if (!opts.enableHorseProfiles) return [];
    var arr = safeRead(profileKey(opts));
    return Array.isArray(arr) ? arr : [];
  }
  function saveProfile(opts, profile) {
    if (!opts.enableHorseProfiles) return;
    var list = loadProfiles(opts).filter(function (p) { return p.id !== profile.id; });
    list.unshift(profile);
    if (list.length > 10) list = list.slice(0, 10);
    safeWrite(profileKey(opts), list);
  }
  function deleteProfile(opts, id) {
    var list = loadProfiles(opts).filter(function (p) { return p.id !== id; });
    safeWrite(profileKey(opts), list);
  }

  // --------------------------------------------------------------------------
  // Shareable result URL — answers are encoded in the location hash so a
  // customer can text/email the link to a barn manager and they land on the
  // same recommendation. We base64-url-encode JSON to keep it short and to
  // tolerate browsers that strip ? params.
  // --------------------------------------------------------------------------
  function b64UrlEncode(str) {
    try {
      var b64 = (typeof btoa === "function")
        ? btoa(unescape(encodeURIComponent(str)))
        : Buffer.from(str, "utf8").toString("base64");
      return b64.replace(/=+$/g, "").replace(/\+/g, "-").replace(/\//g, "_");
    } catch (e) { return ""; }
  }
  function b64UrlDecode(str) {
    try {
      var b64 = String(str).replace(/-/g, "+").replace(/_/g, "/");
      while (b64.length % 4) b64 += "=";
      var raw = (typeof atob === "function")
        ? atob(b64)
        : Buffer.from(b64, "base64").toString("utf8");
      return decodeURIComponent(escape(raw));
    } catch (e) { return null; }
  }
  function encodeAnswers(answers) {
    return b64UrlEncode(JSON.stringify(answers || {}));
  }
  function decodeAnswers(token) {
    var json = b64UrlDecode(token);
    if (!json) return null;
    try { return JSON.parse(json); } catch (e) { return null; }
  }
  function readShareTokenFromLocation() {
    if (typeof root.location === "undefined") return null;
    var hash = root.location.hash || "";
    var m = hash.match(/[#&]ff=([^&]+)/);
    return m ? m[1] : null;
  }
  function buildShareUrl(opts, answers) {
    if (!opts.enableShareLinks || typeof root.location === "undefined") return null;
    var token = encodeAnswers(answers);
    if (!token) return null;
    var loc = root.location;
    // Prefer the canonical fly-finder page when configured. Falls back to the
    // current location so the wizard still produces a usable share URL on
    // the local microsite / unconfigured themes. Either way, strip any
    // existing hash so we don't double-stack `#ff=` tokens.
    var base = String(opts.shareUrlBase || (loc.origin + loc.pathname + loc.search));
    var hashIdx = base.indexOf("#");
    if (hashIdx !== -1) base = base.substring(0, hashIdx);
    return base + "#ff=" + token;
  }

  // --------------------------------------------------------------------------
  // Klaviyo client — public key only. We use the documented client API rather
  // than the private server endpoint so this can run safely in theme JS.
  //   identify -> attaches email + horse profile to the active profile
  //   track    -> records the named event with metric properties
  // No-ops if klaviyoPublicKey is unset or window.klaviyo is unavailable.
  // --------------------------------------------------------------------------
  function klaviyoIdentify(opts, traits) {
    if (!opts.klaviyoPublicKey || !root.klaviyo || typeof root.klaviyo.identify !== "function") return;
    try { root.klaviyo.identify(traits); } catch (e) {}
  }
  function klaviyoTrack(opts, eventName, properties) {
    if (!opts.klaviyoPublicKey || !root.klaviyo || typeof root.klaviyo.track !== "function") return;
    try { root.klaviyo.track(eventName, properties || {}); } catch (e) {}
  }
  // Subscribe the profile to opts.klaviyoListId via the public Client
  // Subscriptions API. Triggers Klaviyo's built-in "Subscribed to List" metric
  // automatically; that metric is what Michelle's marketing flow keys off of.
  // Fire-and-forget: failures don't block the result page or the recommendation
  // email (which fires off the separate "Fly Finder Email Capture" event).
  // Caller is responsible for only invoking this when the shopper has explicitly
  // checked the marketing opt-in box -- this is a SUBSCRIBED-consent signal.
  function klaviyoSubscribe(opts, email) {
    if (!opts.klaviyoPublicKey || !opts.klaviyoListId || !email) return;
    if (typeof root.fetch !== "function") return;
    var url = "https://a.klaviyo.com/client/subscriptions/?company_id=" +
              encodeURIComponent(opts.klaviyoPublicKey);
    var body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: "Fly Fit Finder",
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: email,
                subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: "list", id: opts.klaviyoListId } }
        }
      }
    };
    try {
      root.fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/vnd.api+json",
          "revision": "2024-10-15"
        },
        body: JSON.stringify(body),
        mode: "cors",
        keepalive: true
      });
    } catch (e) {}
  }

  // --------------------------------------------------------------------------
  // Image preloading — kicks off browser cache fetch for the next 1-2 step
  // images before the user sees them, eliminating the visual flash.
  // --------------------------------------------------------------------------
  function preloadImages(opts, basenames) {
    if (typeof root.Image === "undefined") return;
    (basenames || []).forEach(function (b) {
      var url = imageUrl(opts, b);
      if (!url) return;
      try { var img = new root.Image(); img.src = url; } catch (e) {}
    });
  }

  // --------------------------------------------------------------------------
  // Resolution algorithm — runs the 5-table cascade.
  // --------------------------------------------------------------------------
  function resolve(answers, rules) {
    var trace = [];
    var breed = normalizeBreed(answers.breed);

    // 1. State + climate
    var state = (root.FFINDER_ZIP_TO_STATE && root.FFINDER_ZIP_TO_STATE.resolve(answers.zip)) || answers.state || null;
    var climateRow = state ? findInTable(rules.climate, ["state"], { state: state }) : null;
    var climate = climateRow ? climateRow.climate : null;
    trace.push({ step: "climate", state: state, value: climate });

    // 2. Size + horse type override (e.g. tall warmblood snaps to Draft).
    // Two-stage lookup:
    //   a) exact match on (breed, height) — most rows hit here
    //   b) closest-height fallback within the same breed AND same unit
    //      (hands vs inches), so an unusual combo like "Horse type + Pony
    //      breed at 16 hands" still produces a numeric fly_size + a
    //      fly_horse_type instead of dropping out as null. Without this,
    //      Klaviyo / GA / the result UI all see fly_size: None for
    //      out-of-table combos, which broke Joel's 2026-05-29 test session
    //      (Test6 — Pony breed picked under Horse type, no size_rule row).
    var sizeRow = findInTable(rules.size, ["breed", "height"], {
      breed: breed,
      height: answers.height
    });
    var sizeFallback = false;
    if (!sizeRow) {
      sizeRow = findClosestSizeRow(rules.size, breed, answers.height);
      if (sizeRow) sizeFallback = true;
    }
    var fly_horse_type = sizeRow ? sizeRow.fly_horse_type : answers.horse_type;
    var fly_size = sizeRow ? sizeRow.fly_size : null;
    trace.push({
      step: "size",
      fly_horse_type: fly_horse_type,
      fly_size: fly_size,
      fallback: sizeFallback
    });

    // 3. Fit + style
    var fitQuery = {
      fly_horse_type: fly_horse_type,
      breed: breed,
      withers_and_head_carriage: answers.withers_and_head_carriage,
      shoulders: answers.shoulders,
      chest: answers.chest,
      mane: answers.mane,
      horse_age: ageBucket(answers.horse_age)
    };
    var fitRow = findInTable(
      rules.fit,
      ["fly_horse_type", "breed", "withers_and_head_carriage", "shoulders", "chest", "mane", "horse_age"],
      fitQuery
    );
    var fly_fit = fitRow ? fitRow.fly_fit : null;
    var fly_style = fitRow ? fitRow.fly_style : null;
    trace.push({ step: "fit", fly_fit: fly_fit, fly_style: fly_style });

    // 4. Fabric (Calm/Calm vs Calm/Rough — temperament from question, not sensitivity)
    var fabricQuery = {
      fly_horse_type: fly_horse_type,
      climate: climate,
      turnout: answers.turnout,
      temperament: answers.temperament,
      worst_reaction: answers.worst_reaction
    };
    var fabricRow = findInTable(
      rules.fabric,
      ["fly_horse_type", "climate", "turnout", "temperament", "worst_reaction"],
      fabricQuery
    );
    var fly_fabric = fabricRow ? fabricRow.fly_fabric : null;
    trace.push({ step: "fabric", fly_fabric: fly_fabric });

    // 5. Recommendation (sensitivity drives this temperament axis: Calm/Medium/High)
    var recQuery = {
      fly_horse_type: fly_horse_type,
      fly_fit: fly_fit,
      fly_fabric: fly_fabric,
      temperament: answers.sensitivity,
      fly_style: fly_style
    };
    var recRow = findInTable(
      rules.recommendations,
      ["fly_horse_type", "fly_fit", "fly_fabric", "temperament", "fly_style"],
      recQuery
    );
    trace.push({ step: "recommendation", matched: !!recRow });

    var products = recRow ? {
      sheet_1:    { product_number: recRow.p_sheet_1,    group: recRow.g_sheet_1 },
      sheet_2:    { product_number: recRow.p_sheet_2,    group: recRow.g_sheet_2 },
      neck_cover: { product_number: recRow.p_neck_cover, group: recRow.g_neck_cover },
      mask:       { product_number: recRow.p_mask,       group: recRow.g_mask },
      boots:      { product_number: recRow.p_boots,      group: recRow.g_boots }
    } : null;

    return {
      ok: !!recRow,
      derived: {
        state: state,
        climate: climate,
        fly_horse_type: fly_horse_type,
        fly_size: fly_size,
        fly_fit: fly_fit,
        fly_style: fly_style,
        fly_fabric: fly_fabric
      },
      products: products,
      trace: trace
    };
  }

  // --------------------------------------------------------------------------
  // Resolve product slots through the Schneider-number -> Shopify-handle map.
  // Walks the matching ProductGroupReplace group when the primary is sold out.
  // --------------------------------------------------------------------------
  function resolveSlot(slot, productMap, productGroups, opts) {
    if (!slot || !slot.product_number) return null;
    var primary = String(slot.product_number);
    var candidates = [primary];
    if (slot.group && productGroups && productGroups[slot.group]) {
      productGroups[slot.group].forEach(function (id) {
        if (id == null) return;
        var s = String(id);
        if (candidates.indexOf(s) === -1) candidates.push(s);
      });
    }
    var products = (productMap && productMap.products) || {};
    // First pass: prefer in-stock mapped entries (including walking the group).
    for (var i = 0; i < candidates.length; i++) {
      var pid = candidates[i];
      var entry = products[pid];
      if (entry && entry.available !== false && entry.handle) {
        return {
          product_number: pid,
          handle: entry.handle,
          name: entry.name || null,
          image: entry.image || null,
          url: opts.productUrlBase + entry.handle,
          source: i === 0 ? "mapped" : "mapped-fallback"
        };
      }
    }
    // Second pass: a mapped-but-sold-out entry still gives a real PDP to link to.
    for (var j = 0; j < candidates.length; j++) {
      var pid2 = candidates[j];
      var entry2 = products[pid2];
      if (entry2 && entry2.handle) {
        return {
          product_number: pid2,
          handle: entry2.handle,
          name: entry2.name || null,
          image: entry2.image || null,
          url: opts.productUrlBase + entry2.handle,
          source: "mapped-out-of-stock"
        };
      }
    }
    // Last resort: search by product number so the customer lands somewhere.
    return {
      product_number: primary,
      handle: null,
      name: null,
      image: null,
      url: opts.fallbackSearchUrl + encodeURIComponent(primary),
      source: "search-fallback"
    };
  }

  // --------------------------------------------------------------------------
  // Render — minimal vanilla DOM, no innerHTML for user input
  // --------------------------------------------------------------------------
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === "class") node.className = attrs[k];
        else if (k === "dataset") Object.assign(node.dataset, attrs[k]);
        else if (k.indexOf("on") === 0 && typeof attrs[k] === "function") node.addEventListener(k.slice(2), attrs[k]);
        else if (k === "html") node.innerHTML = attrs[k];
        else node.setAttribute(k, attrs[k]);
      });
    }
    (children || []).forEach(function (c) {
      if (c == null) return;
      if (typeof c === "string") node.appendChild(document.createTextNode(c));
      else node.appendChild(c);
    });
    return node;
  }

  // ----------------------------------------------------------------------------
  // Complete event payload for GA4 + Klaviyo. Pure function — no DOM, no
  // network. `slots` is the array of { def, card } produced by mapping the
  // RESULT_SLOTS over `resolveSlot()`. Tests use this directly to assert the
  // shape called out in FLY_FINDER_KLAVIYO_FLOWS.md.
  // ----------------------------------------------------------------------------
  function buildCompletePayload(answers, resolved, slots) {
    var derived = resolved.derived || {};
    var derivedNarrative = {
      horse_type: narrate("horse_type", derived.fly_horse_type),
      fit: derived.fly_fit || "",
      fabric: derived.fly_fabric || "",
      climate: derived.climate || "",
      style: derived.fly_style || "",
      size: derived.fly_size || "",
      temperament: narrate("temperament", answers.temperament),
      turnout: narrate("turnout", answers.turnout),
      worst_reaction: narrate("worst_reaction", answers.worst_reaction),
      sensitivity: narrate("sensitivity", answers.sensitivity)
    };

    // Build recommended_products once — used for the GA4 fly_finder_complete
    // event AND the Klaviyo metric. Retention spec calls for product_url +
    // product_image_url + product_title so the email template can render
    // rich product cards without a separate Shopify lookup.
    var recommendedProducts = (slots || []).map(function (s) {
      var card = s.card || {};
      return {
        slot: (s.def || {}).key,
        product_number: card.product_number,
        product_title: card.name || ("Product #" + card.product_number),
        product_url: card.url,
        product_image_url: card.image || null,
        handle: card.handle || null,
        source: card.source
      };
    });

    var horseSummarySentence = buildHorseSummary(answers, derived);

    var payload = Object.assign({}, derived, {
      horse_name: answers.horse_name || null,
      horse_summary_sentence: horseSummarySentence,
      narrative: derivedNarrative,
      // Legacy `slots` shape preserved for older Klaviyo flows that already
      // reference event.slots[*].product_number / handle / source.
      slots: recommendedProducts.map(function (p) {
        return { slot: p.slot, product_number: p.product_number, handle: p.handle, source: p.source };
      }),
      recommended_products: recommendedProducts
    });

    return { payload: payload, narrative: derivedNarrative, recommended_products: recommendedProducts };
  }

  function FlyFitFinder(rootEl, opts) {
    this.root = rootEl;
    this.opts = Object.assign({}, DEFAULTS, opts || {});
    this.state = {
      stepIndex: 0,
      answers: {},
      started: false,
      completed: false,
      // 'wizard' | 'profile-picker' | 'result' | 'shared' | 'profile-review'
      mode: "wizard",
      // Snapshot of in-progress wizard captured before viewing a saved
      // profile's recommendation, so we can return without loss.
      savedSnapshot: null
    };
    this.rules = null;
    this.productMap = null;
    this.abandonHandlerInstalled = false;
  }

  FlyFitFinder.prototype.installAbandonHandler = function () {
    if (this.abandonHandlerInstalled || !this.opts.enableAbandonTracking) return;
    var self = this;
    var fired = false;
    var handler = function () {
      if (fired) return;
      if (!self.state.started || self.state.completed) return;
      fired = true;
      var step = STEPS[self.state.stepIndex] || {};
      emit("fly_finder_abandon", {
        last_step_index: self.state.stepIndex,
        last_step_id: step.id || null,
        answered: Object.keys(self.state.answers).length
      });
      // Fire-and-forget Klaviyo if we already captured an email.
      if (self.state.answers.email) {
        klaviyoTrack(self.opts, "Fly Finder Abandoned", {
          last_step_id: step.id || null,
          last_step_index: self.state.stepIndex,
          horse_name: self.state.answers.horse_name || null
        });
      }
    };
    if (typeof root.addEventListener === "function") {
      root.addEventListener("pagehide", handler);
      root.addEventListener("beforeunload", handler);
    }
    this.abandonHandlerInstalled = true;
  };

  FlyFitFinder.prototype.mount = function () {
    var self = this;
    this.root.classList.add("ffinder");
    this.renderLoading();

    // Section-level keyboard handler. Esc steps backward when not at root.
    if (!this.root.dataset.ffinderKeyHandlerInstalled) {
      this.root.addEventListener("keydown", function (e) {
        if (e.key === "Escape") {
          if (self.state.mode === "wizard" && self.state.stepIndex > 0) {
            e.preventDefault();
            self.prev();
          }
        }
      });
      this.root.dataset.ffinderKeyHandlerInstalled = "1";
    }

    // Auto-detect customer scope from window.Shopify.customer_id_from_theme
    // when host page doesn't pass it explicitly.
    if (this.opts.customerId == null && root.Shopify && root.Shopify.customer && root.Shopify.customer.id) {
      this.opts.customerId = String(root.Shopify.customer.id);
    }

    var loaders = [fetchJSON(this.opts.rulesUrl)];
    if (this.opts.productMapUrl) loaders.push(fetchJSON(this.opts.productMapUrl).catch(function () { return null; }));

    Promise.all(loaders)
      .then(function (results) {
        var raw = results[0];
        self.rules = {
          size:            expandColumnar(raw.size_rules),
          climate:         expandColumnar(raw.climate_rules),
          fit:             expandColumnar(raw.fit_rules),
          fabric:          expandColumnar(raw.fabric_rules),
          recommendations: expandColumnar(raw.recommendations),
          product_groups:  raw.product_groups || {}
        };
        self.productMap = results[1] || null;
        self.installAbandonHandler();
        self.routeInitialView();
      })
      .catch(function (err) {
        self.renderError(err);
      });
  };

  /**
   * Picks the initial screen on mount:
   *   1. If location hash has an `ff=` share token, render the result view directly.
   *   2. Else if we have one or more saved profiles or a saved session, show the
   *      profile / resume picker so the user can continue or start fresh.
   *   3. Else render step 0.
   */
  FlyFitFinder.prototype.routeInitialView = function () {
    var token = readShareTokenFromLocation();
    if (token) {
      var shared = decodeAnswers(token);
      if (shared && typeof shared === "object") {
        this.state.answers = shared;
        this.state.stepIndex = STEPS.length - 1;
        this.state.started = true;
        this.state.completed = true;
        this.state.mode = "shared";
        emit("fly_finder_share_open", { fields: Object.keys(shared) });
        this.renderResult();
        return;
      }
    }
    var profiles = loadProfiles(this.opts);
    var session = loadSession(this.opts);
    if ((profiles && profiles.length) || (session && session.answers && Object.keys(session.answers).length > 0)) {
      this.state.mode = "profile-picker";
      this.renderProfilePicker(profiles, session);
      return;
    }
    this.renderStep();
  };

  FlyFitFinder.prototype.renderLoading = function () {
    this.root.replaceChildren();
    this.root.appendChild(el("div", { class: "ffinder-loading" }, ["Loading the Fly Fit Finder…"]));
  };

  FlyFitFinder.prototype.renderError = function (err) {
    this.root.replaceChildren();
    // Detect the most common "broken locally" case: opening network/index.html
    // by double-clicking from M:\, which makes fetch() of the rules JSON fail
    // because browsers block file:// requests for security. Show a developer
    // hint instead of the generic store-front fallback.
    var loc = (root && root.location) || {};
    var isFileProtocol = loc.protocol === "file:";
    if (isFileProtocol) {
      this.root.appendChild(el("div", { class: "ffinder-error ffinder-error--dev", role: "alert" }, [
        el("strong", {}, ["Local preview can't load the wizard from file://."]),
        el("br", {}),
        el("br", {}),
        "Browsers block ",
        el("code", {}, ["fetch()"]),
        " of local JSON for security. Run a tiny HTTP server instead:",
        el("br", {}),
        el("br", {}),
        el("code", { class: "ffinder-error__cmd" }, ["python serve_network.py"]),
        el("br", {}),
        el("br", {}),
        "From the project root (",
        el("code", {}, ["M:\\Cursor_Caitlin\\websites\\Fly Fit Finder"]),
        "). The script opens ",
        el("code", {}, ["http://localhost:8080/"]),
        " automatically.",
      ]));
    } else {
      this.root.appendChild(el("div", { class: "ffinder-error", role: "alert" }, [
        "We couldn't load the Fly Fit Finder right now. ",
        el("a", { href: this.opts.collectionUrl }, ["Browse all fly sheets"]),
        "."
      ]));
    }
    if (root.console) root.console.error("[FlyFitFinder]", err);
  };

  FlyFitFinder.prototype.totalSteps = function () { return STEPS.length; };

  FlyFitFinder.prototype.heightOptions = function () {
    var t = this.state.answers.horse_type;
    return HEIGHT_OPTIONS_BY_TYPE[t] || HEIGHT_OPTIONS_BY_TYPE.Horse;
  };

  FlyFitFinder.prototype.fireStartIfNeeded = function () {
    if (this.state.started) return;
    this.state.started = true;
    emit("fly_finder_start", {});
  };

  FlyFitFinder.prototype.setAnswer = function (id, value) {
    this.fireStartIfNeeded();
    this.state.answers[id] = value;
    emit("fly_finder_step", { step: this.state.stepIndex + 1, field: id, value: value });
    saveSession(this.opts, this.state);
  };

  FlyFitFinder.prototype.canAdvance = function () {
    var step = STEPS[this.state.stepIndex];
    if (step.optional) return true;
    var v = this.state.answers[step.id];
    return v != null && v !== "";
  };

  FlyFitFinder.prototype.next = function () {
    if (!this.canAdvance()) return;
    if (this.state.stepIndex >= STEPS.length - 1) {
      this.renderResult();
      return;
    }
    this.state.stepIndex += 1;
    saveSession(this.opts, this.state);
    this.renderStep();
  };

  FlyFitFinder.prototype.prev = function () {
    if (this.state.stepIndex === 0) return;
    this.state.stepIndex -= 1;
    saveSession(this.opts, this.state);
    this.renderStep();
  };

  FlyFitFinder.prototype.startFresh = function () {
    this.state.answers = {};
    this.state.stepIndex = 0;
    this.state.started = false;
    this.state.completed = false;
    this.state.mode = "wizard";
    this.state.savedSnapshot = null;
    clearSession(this.opts);
    this.renderStep();
  };

  /**
   * Show a saved profile's resolved recommendation WITHOUT clobbering the
   * customer's in-progress wizard state. Den 2026-05-13 finding #2: clicking
   * "See recommendation" used to overwrite the in-progress session and the
   * customer lost their work. Now we snapshot the current wizard, switch
   * into 'profile-review' mode, and renderResult() prepends a Back button
   * that restores the snapshot.
   */
  FlyFitFinder.prototype.viewSavedProfileResult = function (profile) {
    if (!profile || !profile.answers) return;
    var current = this.state.answers || {};
    var hasInProgress = this.state.started && Object.keys(current).length > 0 && !this.state.completed;
    this.state.savedSnapshot = hasInProgress
      ? { answers: Object.assign({}, current), stepIndex: this.state.stepIndex }
      : null;
    this.state.answers = Object.assign({}, profile.answers);
    this.state.stepIndex = STEPS.length - 1;
    this.state.completed = true;
    this.state.mode = "profile-review";
    // Intentionally NO saveSession() — we want the in-progress wizard
    // (if any) to survive untouched in storage.
    emit("fly_finder_resume", { step: this.state.stepIndex, source: "profile-review" });
    this.renderResult();
  };

  FlyFitFinder.prototype.resumeWith = function (answers, stepIndex) {
    this.state.answers = Object.assign({}, answers || {});
    this.state.stepIndex = Math.max(0, Math.min(STEPS.length - 1, stepIndex || 0));
    this.state.started = true;
    this.state.completed = false;
    this.state.mode = "wizard";
    this.state.savedSnapshot = null;
    saveSession(this.opts, this.state);
    emit("fly_finder_resume", { step: this.state.stepIndex, source: "profile-picker" });
    this.renderStep();
  };

  FlyFitFinder.prototype.renderProfilePicker = function (profiles, session) {
    var self = this;
    this.root.replaceChildren();
    var hasSession = session && session.answers && Object.keys(session.answers).length > 0;
    var sessionStep = hasSession ? Math.min(session.stepIndex || 0, STEPS.length - 1) : 0;
    var sessionHorse = hasSession ? (session.answers.horse_name || "your in-progress finder") : null;

    var card = el("div", { class: "ffinder-card ffinder-picker" }, [
      el("h2", { class: "ffinder-question" }, ["Welcome back."]),
      el("p", { class: "ffinder-help" }, ["Pick up where you left off, or start fresh for a new horse."])
    ]);

    if (hasSession) {
      card.appendChild(el("div", { class: "ffinder-picker__row" }, [
        el("div", { class: "ffinder-picker__label" }, [
          el("strong", {}, ["Continue " + sessionHorse + "'s finder"]),
          el("div", { class: "ffinder-picker__sub" }, ["Step " + (sessionStep + 1) + " of " + STEPS.length])
        ]),
        el("button", {
          type: "button",
          class: "ffinder-btn ffinder-btn--primary",
          onclick: function () { self.resumeWith(session.answers, session.stepIndex); }
        }, ["Continue"])
      ]));
    }

    (profiles || []).forEach(function (p) {
      card.appendChild(el("div", { class: "ffinder-picker__row" }, [
        el("div", { class: "ffinder-picker__label" }, [
          el("strong", {}, [p.name || "Saved horse"]),
          el("div", { class: "ffinder-picker__sub" }, ["Last updated " + new Date(p.savedAt || Date.now()).toLocaleDateString()])
        ]),
        el("div", { class: "ffinder-picker__actions" }, [
          el("button", {
            type: "button",
            class: "ffinder-btn ffinder-btn--ghost",
            onclick: function () { self.viewSavedProfileResult(p); }
          }, ["See recommendation"]),
          el("button", {
            type: "button",
            class: "ffinder-btn ffinder-btn--ghost",
            onclick: function () {
              if (root.confirm && !root.confirm("Delete saved profile for " + (p.name || "this horse") + "?")) return;
              deleteProfile(self.opts, p.id);
              self.routeInitialView();
            }
          }, ["Delete"])
        ])
      ]));
    });

    card.appendChild(el("div", { class: "ffinder-picker__footer" }, [
      el("button", {
        type: "button",
        class: "ffinder-btn ffinder-btn--ghost",
        onclick: function () { self.startFresh(); }
      }, ["Start a new horse"])
    ]));

    this.root.appendChild(card);
  };

  FlyFitFinder.prototype.renderStep = function () {
    var step = STEPS[this.state.stepIndex];
    if (step && step.type === "email-capture") {
      return this.renderEmailCaptureStep();
    }
    var self = this;
    var answers = this.state.answers;
    this.root.replaceChildren();

    var pct = Math.round(((this.state.stepIndex) / (STEPS.length - 1)) * 100);
    this.root.appendChild(el("div", { class: "ffinder-progress", role: "progressbar", "aria-valuenow": String(pct), "aria-valuemin": "0", "aria-valuemax": "100" }, [
      el("div", { class: "ffinder-progress__bar", style: "width:" + pct + "%" })
    ]));

    var personalizedLabel = step.label
      .replace("your horse's", answers.horse_name ? (answers.horse_name + "'s") : "your horse's")
      .replace("your horse", answers.horse_name || "your horse");

    var card = el("div", { class: "ffinder-card", tabindex: "-1" }, [
      el("div", { class: "ffinder-step-meta" }, ["Step " + (this.state.stepIndex + 1) + " of " + STEPS.length]),
      el("h2", { class: "ffinder-question" }, [personalizedLabel]),
      step.help ? el("p", { class: "ffinder-help" }, [step.help]) : null
    ]);

    // Soft sanity warning when breed and horse_type look mismatched.
    if (step.id === "breed" || step.id === "height") {
      var warning = breedTypeWarning(answers);
      if (warning) {
        card.appendChild(el("div", { class: "ffinder-warning", role: "note" }, [
          el("strong", {}, ["Quick check: "]),
          warning
        ]));
      }
    }

    var input = this.renderInput(step);
    card.appendChild(input);

    // 'How to measure' helper for physical-trait questions.
    if (step.measure) {
      var details = el("details", { class: "ffinder-measure" });
      details.appendChild(el("summary", {}, ["How do I tell?"]));
      details.appendChild(el("p", { class: "ffinder-measure__body" }, [step.measure]));
      if (this.opts.measuringGuideUrl) {
        details.appendChild(el("p", { class: "ffinder-measure__body" }, [
          el("a", { href: this.opts.measuringGuideUrl, target: "_blank", rel: "noopener" }, ["See Schneiders full measuring guide →"])
        ]));
      }
      card.appendChild(details);
    }

    var nav = el("div", { class: "ffinder-nav" }, [
      this.state.stepIndex > 0 ? el("button", { type: "button", class: "ffinder-btn ffinder-btn--ghost", onclick: function () { self.prev(); } }, ["Back"]) : null,
      el("button", {
        type: "button",
        class: "ffinder-btn ffinder-btn--primary",
        onclick: function () { self.next(); },
        "aria-disabled": this.canAdvance() ? "false" : "true"
      }, [this.state.stepIndex === STEPS.length - 1 ? "See my recommendations" : "Next"])
    ]);
    card.appendChild(nav);

    this.root.appendChild(card);

    // Focus the card so screen readers + keyboard users land in the right place
    // when navigating between steps. Use a microtask to let layout settle.
    setTimeout(function () { try { card.focus(); } catch (e) {} }, 0);

    // Preload the next step's image-radio thumbnails to avoid first-paint flash.
    var nextStep = STEPS[this.state.stepIndex + 1];
    if (nextStep && nextStep.type === "image-radio") {
      preloadImages(this.opts, (nextStep.options || []).map(function (o) { return o.image; }).filter(Boolean));
    }
    var afterNext = STEPS[this.state.stepIndex + 2];
    if (afterNext && afterNext.type === "image-radio") {
      preloadImages(this.opts, (afterNext.options || []).map(function (o) { return o.image; }).filter(Boolean));
    }
  };

  // ---------------------------------------------------------------------------
  // Step 15 — soft email gate. Computes the recommendation up-front so the
  // Klaviyo "Fly Finder Email Capture" payload includes recommended_products[]
  // (Michelle 2026-05-21: flow needs product list at submit time so the recap
  // email can render product cards). Skip path bypasses Klaviyo entirely.
  // ---------------------------------------------------------------------------
  FlyFitFinder.prototype.renderEmailCaptureStep = function () {
    var self = this;
    var step = STEPS[this.state.stepIndex];
    var answers = this.state.answers;
    this.root.replaceChildren();

    var pct = Math.round(((this.state.stepIndex) / (STEPS.length - 1)) * 100);
    this.root.appendChild(el("div", { class: "ffinder-progress", role: "progressbar", "aria-valuenow": String(pct), "aria-valuemin": "0", "aria-valuemax": "100" }, [
      el("div", { class: "ffinder-progress__bar", style: "width:" + pct + "%" })
    ]));

    // Pre-resolve so we have the same payload Klaviyo would receive on the
    // result page. Mirrors the slot-construction shape used by renderResult
    // so buildCompletePayload sees the exact same input.
    var resolved = resolve(answers, this.rules);
    var slots = resolved.ok ? RESULT_SLOTS.map(function (slotDef) {
      var slot = resolved.products[slotDef.key];
      var card = resolveSlot(slot, self.productMap, self.rules.product_groups, self.opts);
      return { def: slotDef, card: card };
    }).filter(function (s) { return s.card; }) : [];
    var built = buildCompletePayload(answers, resolved, slots);
    var completePayload = built.payload;

    var horseName = answers.horse_name && String(answers.horse_name).trim();
    var heading = horseName
      ? "Want a copy of " + horseName + "'s recommendations?"
      : step.label;

    var card = el("div", { class: "ffinder-card", tabindex: "-1" }, [
      el("div", { class: "ffinder-step-meta" }, ["Step " + (this.state.stepIndex + 1) + " of " + STEPS.length]),
      el("h2", { class: "ffinder-question" }, [heading]),
      step.help ? el("p", { class: "ffinder-help" }, [step.help]) : null
    ]);

    var status = el("div", { class: "ffinder-email__status", role: "status", "aria-live": "polite" });
    var primary;
    var input = el("input", {
      type: "email",
      class: "ffinder-input",
      id: "ffinder-email_capture",
      name: "email",
      placeholder: "you@example.com",
      autocomplete: "email",
      "aria-label": "Email address",
      value: answers.email || "",
      oninput: function (e) {
        answers.email = e.target.value;
        if (status.textContent) status.textContent = "";
      },
      onkeydown: function (e) { if (e.key === "Enter") { e.preventDefault(); if (primary) primary.click(); } }
    });
    var checkbox = el("input", { type: "checkbox", checked: "checked" });
    var optin = el("label", { class: "ffinder-email__optin" }, [
      checkbox,
      el("span", {}, ["Also send me Schneiders seasonal fly-care tips."])
    ]);

    card.appendChild(el("div", { class: "ffinder-field" }, [input]));
    card.appendChild(optin);
    card.appendChild(status);

    primary = el("button", {
      type: "button",
      class: "ffinder-btn ffinder-btn--primary",
      onclick: function () {
        var email = String(input.value || "").trim();
        if (!email) {
          // Empty submit = treat as skip so we don't yell at users who didn't
          // notice the Skip link. Friendlier UX, same outcome.
          self.state.emailCapturedAtStep = false;
          emit("fly_finder_step15_skip", { reason: "blank_submit" });
          self.renderResult();
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          status.textContent = "Please enter a valid email address.";
          input.focus();
          return;
        }
        var optedIn = !!checkbox.checked;
        answers.email = email;
        answers.marketing_optin = optedIn;
        self.state.emailCapturedAtStep = true;
        klaviyoIdentify(self.opts, {
          $email: email,
          horse_name: answers.horse_name || null,
          fly_finder_last_size: completePayload.fly_size || null,
          fly_finder_last_fit: completePayload.fly_fit || null,
          fly_finder_last_fabric: completePayload.fly_fabric || null,
          fly_finder_marketing_optin: optedIn
        });
        klaviyoTrack(self.opts, "Fly Finder Email Capture", Object.assign({
          email: email,
          marketing_optin: optedIn,
          share_url: buildShareUrl(self.opts, answers),
          capture_point: "step_15"
        }, completePayload));
        if (optedIn) klaviyoSubscribe(self.opts, email);
        emit("fly_finder_email_submit", { marketing_optin: optedIn, capture_point: "step_15" });
        self.renderResult();
      }
    }, ["See my recommendations"]);

    var nav = el("div", { class: "ffinder-nav ffinder-nav--email" }, [
      this.state.stepIndex > 0 ? el("button", { type: "button", class: "ffinder-btn ffinder-btn--ghost", onclick: function () { self.prev(); } }, ["Back"]) : null,
      primary
    ]);
    card.appendChild(nav);

    var skip = el("button", {
      type: "button",
      class: "ffinder-skip-link",
      onclick: function () {
        self.state.emailCapturedAtStep = false;
        emit("fly_finder_step15_skip", { reason: "skip_clicked" });
        self.renderResult();
      }
    }, ["No thanks, just show me the recommendations"]);
    card.appendChild(el("div", { class: "ffinder-skip" }, [skip]));

    this.root.appendChild(card);
    setTimeout(function () { try { card.focus(); } catch (e) {} }, 0);
  };

  FlyFitFinder.prototype.renderInput = function (step) {
    var self = this;
    var answers = this.state.answers;

    if (step.type === "text" || step.type === "zip" || step.type === "number") {
      var existing = answers[step.id] || "";
      var enableNext = function () {
        var btn = self.root.querySelector(".ffinder-btn--primary");
        if (btn) btn.setAttribute("aria-disabled", self.canAdvance() ? "false" : "true");
      };
      var input = el("input", {
        type: step.type === "number" ? "number" : (step.type === "zip" ? "tel" : "text"),
        class: "ffinder-input",
        id: "ffinder-" + step.id,
        name: step.id,
        placeholder: step.placeholder || "",
        value: existing,
        autocomplete: step.type === "zip" ? "postal-code" : "off",
        oninput: function (e) {
          self.setAnswer(step.id, e.target.value);
          enableNext();
        },
        onkeydown: function (e) {
          if (e.key === "Enter") { e.preventDefault(); self.next(); }
        }
      });
      if (step.type === "number") {
        if (step.min != null) input.min = String(step.min);
        if (step.max != null) input.max = String(step.max);
      }
      return el("div", { class: "ffinder-field" }, [input]);
    }

    if (step.type === "select") {
      var options = step.dynamic === "height_options" ? this.heightOptions() : (step.options || []);
      var syncNav = function () {
        var btn = self.root.querySelector(".ffinder-btn--primary");
        if (btn) btn.setAttribute("aria-disabled", self.canAdvance() ? "false" : "true");
      };
      var sel = el("select", {
        class: "ffinder-select",
        id: "ffinder-" + step.id,
        name: step.id,
        onchange: function (e) {
          self.setAnswer(step.id, e.target.value);
          syncNav();
        },
        onkeydown: function (e) {
          // Enter advances when a value is already selected.
          if (e.key === "Enter" && self.canAdvance()) { e.preventDefault(); self.next(); }
        }
      });
      sel.appendChild(el("option", { value: "" }, ["— Select —"]));
      options.forEach(function (o) {
        var v = typeof o === "string" ? o : o.value;
        var l = typeof o === "string" ? o : o.label;
        var opt = el("option", { value: v }, [l]);
        if (String(answers[step.id]) === String(v)) opt.selected = true;
        sel.appendChild(opt);
      });
      return el("div", { class: "ffinder-field" }, [sel]);
    }

    if (step.type === "image-radio") {
      var grid = el("div", { class: "ffinder-grid", role: "radiogroup", "aria-label": step.label });
      var cards = [];
      step.options.forEach(function (opt, idx) {
        var checked = answers[step.id] === opt.value;
        var card = el("button", {
          type: "button",
          class: "ffinder-choice" + (checked ? " is-selected" : ""),
          role: "radio",
          "aria-checked": checked ? "true" : "false",
          tabindex: checked || (idx === 0 && answers[step.id] == null) ? "0" : "-1",
          onclick: function () {
            self.setAnswer(step.id, opt.value);
            self.renderStep();
          },
          onkeydown: function (e) {
            if (e.key === "ArrowRight" || e.key === "ArrowDown") {
              e.preventDefault();
              var nxt = cards[(idx + 1) % cards.length];
              if (nxt) nxt.focus();
            } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
              e.preventDefault();
              var prev = cards[(idx - 1 + cards.length) % cards.length];
              if (prev) prev.focus();
            } else if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              self.setAnswer(step.id, opt.value);
              self.renderStep();
            }
          }
        }, [
          opt.image ? el("img", { class: "ffinder-choice__img", src: imageUrl(self.opts, opt.image), alt: opt.label, loading: "lazy" }) : null,
          el("span", { class: "ffinder-choice__label" }, [opt.label])
        ]);
        cards.push(card);
        grid.appendChild(card);
      });
      return grid;
    }

    return el("div", {}, ["(unsupported step type: " + step.type + ")"]);
  };

  FlyFitFinder.prototype.renderResult = function () {
    var self = this;
    var answers = this.state.answers;
    var resolved = resolve(answers, this.rules);
    this.state.completed = true;

    var horseName = answers.horse_name || "your horse";
    this.root.replaceChildren();

    if (!resolved.ok) {
      emit("fly_finder_no_match", { trace: resolved.trace, horse_type: answers.horse_type || null });
      this.root.appendChild(el("div", { class: "ffinder-card ffinder-result ffinder-result--no-match" }, [
        el("h2", { class: "ffinder-question" }, ["We couldn't find a perfect match for " + horseName + "."]),
        el("p", { class: "ffinder-help" }, [
          "Our fit team can help — in the meantime, ",
          el("a", { href: self.opts.collectionUrl }, ["browse the full fly sheet collection"]),
          "."
        ]),
        el("div", { class: "ffinder-result__actions" }, [
          el("button", { type: "button", class: "ffinder-btn ffinder-btn--ghost", onclick: function () { self.startFresh(); } }, ["Start over"]),
          self.opts.fitSpecialistUrl ? el("a", { class: "ffinder-btn ffinder-btn--primary", href: self.opts.fitSpecialistUrl }, ["Talk to a fit specialist"]) : null,
          el("a", { class: "ffinder-btn ffinder-btn--primary", href: self.opts.collectionUrl }, ["See all fly sheets"])
        ])
      ]));
      return;
    }

    var slots = RESULT_SLOTS.map(function (slotDef) {
      var slot = resolved.products[slotDef.key];
      var card = resolveSlot(slot, self.productMap, self.rules.product_groups, self.opts);
      return { def: slotDef, card: card };
    }).filter(function (s) { return s.card; });

    var built = buildCompletePayload(answers, resolved, slots);
    var derivedNarrative = built.narrative;
    var completePayload = built.payload;

    var isProfileReview = self.state.mode === "profile-review";

    if (!isProfileReview) {
      emit("fly_finder_complete", completePayload);
      klaviyoTrack(self.opts, "Fly Finder Completed", completePayload);

      // Persist horse profile (named only) for next visit. We dedupe by a
      // normalized horse_name so re-running the wizard for the same horse —
      // including via "See recommendation" from the profile picker — updates
      // the existing entry in place rather than appending a duplicate.
      if (answers.horse_name) {
        var nameKey = String(answers.horse_name).trim().toLowerCase();
        if (nameKey) {
          saveProfile(self.opts, {
            id: "name:" + nameKey,
            name: answers.horse_name,
            savedAt: Date.now(),
            answers: answers
          });
        }
      }
      clearSession(self.opts);
    }

    var summaryChildren = [
      "Based on a ", el("strong", {}, [derivedNarrative.horse_type || "horse"]),
      derivedNarrative.size ? (" sized " + derivedNarrative.size + ", ") : ", ",
      "with a ", el("strong", {}, [derivedNarrative.fit || "matching"]), " fit, ",
      el("strong", {}, [derivedNarrative.style]), " closure, in ",
      el("strong", {}, [derivedNarrative.fabric || "the right fabric"]),
      " for a ", el("strong", {}, [derivedNarrative.climate || "your"]), " climate"
    ];
    if (resolved.derived.state) {
      summaryChildren.push(" in ", el("strong", {}, [resolved.derived.state]));
    }
    summaryChildren.push(". ");
    if (derivedNarrative.temperament) {
      summaryChildren.push(
        "Tuned for a horse who's " + derivedNarrative.temperament +
        (derivedNarrative.turnout ? (", out " + derivedNarrative.turnout) : "") + "."
      );
    }

    var summary = el("div", { class: "ffinder-result__summary" }, [
      el("h2", { class: "ffinder-question" }, ["Here's what we recommend for " + horseName + "."]),
      el("p", { class: "ffinder-help" }, summaryChildren)
    ]);

    var grid = el("div", { class: "ffinder-results" });
    slots.forEach(function (s) {
      var card = el("a", {
        class: "ffinder-product" + (s.def.primary ? " ffinder-product--primary" : ""),
        href: s.card.url,
        target: "_top",
        onclick: function () {
          emit("fly_finder_outbound_click", {
            slot: s.def.key,
            product_number: s.card.product_number,
            handle: s.card.handle,
            source: s.card.source
          });
        }
      }, [
        s.card.image ? el("img", { class: "ffinder-product__img", src: s.card.image, alt: s.card.name || s.def.title, loading: "lazy" }) : null,
        el("div", { class: "ffinder-product__body" }, [
          el("div", { class: "ffinder-product__role" }, [s.def.title]),
          el("div", { class: "ffinder-product__name" }, [s.card.name || ("Product #" + s.card.product_number)]),
          el("div", { class: "ffinder-product__cta" }, ["View product →"])
        ])
      ]);
      grid.appendChild(card);
    });

    // Build a search-link that lists every matching fly sheet in the resolved
    // fit + fabric. Lets the customer browse beyond the 5 recommendations.
    var matchingSearchUrl = null;
    var fitTerm = resolved.derived.fly_fit || "";
    var fabricTerm = resolved.derived.fly_fabric || "";
    if (fitTerm || fabricTerm) {
      var terms = [fitTerm, fabricTerm, "fly sheet"].filter(function (t) { return t; }).join(" ");
      matchingSearchUrl = "/search?q=" + encodeURIComponent(terms) + "&type=product";
    }

    var actionsRow = [
      el("button", { type: "button", class: "ffinder-btn ffinder-btn--ghost", onclick: function () { self.startFresh(); } }, ["Start over"])
    ];
    if (matchingSearchUrl) {
      actionsRow.push(el("a", {
        class: "ffinder-btn ffinder-btn--primary",
        href: matchingSearchUrl
      }, ["See all matching fly sheets"]));
    }
    actionsRow.push(el("a", { class: "ffinder-btn ffinder-btn--ghost", href: self.opts.collectionUrl }, ["Browse the full collection"]));
    if (self.opts.fitSpecialistUrl) {
      actionsRow.push(el("a", { class: "ffinder-btn ffinder-btn--ghost", href: self.opts.fitSpecialistUrl }, ["Talk to a fit specialist"]));
    }
    var actions = el("div", { class: "ffinder-result__actions" }, actionsRow);

    var card = el("div", { class: "ffinder-card ffinder-result" }, []);

    // Profile-review banner: when the user clicked "See recommendation" on a
    // saved horse profile, we keep their in-progress wizard intact and offer
    // a one-click way back. (Den 2026-05-13 finding #2.)
    if (isProfileReview && self.state.savedSnapshot) {
      var snapshot = self.state.savedSnapshot;
      var snapHorse = (snapshot.answers && snapshot.answers.horse_name) || "your in-progress finder";
      card.appendChild(el("div", { class: "ffinder-result__back" }, [
        el("button", {
          type: "button",
          class: "ffinder-btn ffinder-btn--ghost",
          onclick: function () {
            self.state.answers = Object.assign({}, snapshot.answers || {});
            self.state.stepIndex = Math.max(0, Math.min(STEPS.length - 1, snapshot.stepIndex || 0));
            self.state.completed = false;
            self.state.mode = "wizard";
            self.state.savedSnapshot = null;
            saveSession(self.opts, self.state);
            self.renderStep();
          }
        }, ["\u2190 Back to " + snapHorse + "'s in-progress finder"])
      ]));
    }

    card.appendChild(summary);

    // Share URL strip — copy-to-clipboard with native fallback.
    if (self.opts.enableShareLinks) {
      var shareUrl = buildShareUrl(self.opts, answers);
      if (shareUrl) {
        var shareInput = el("input", {
          type: "text",
          class: "ffinder-share__input",
          readonly: "readonly",
          value: shareUrl,
          "aria-label": "Shareable result URL"
        });
        var shareBtn = el("button", {
          type: "button",
          class: "ffinder-btn ffinder-btn--ghost",
          onclick: function () {
            shareInput.focus();
            shareInput.select();
            var ok = false;
            try { ok = root.navigator && root.navigator.clipboard && typeof root.navigator.clipboard.writeText === "function"; } catch (e) {}
            if (ok) {
              root.navigator.clipboard.writeText(shareUrl).then(function () {
                shareBtn.textContent = "Copied!";
                setTimeout(function () { shareBtn.textContent = "Copy link"; }, 1800);
              }).catch(function () { document.execCommand && document.execCommand("copy"); });
            } else if (document.execCommand) {
              try { document.execCommand("copy"); shareBtn.textContent = "Copied!"; setTimeout(function () { shareBtn.textContent = "Copy link"; }, 1800); } catch (e) {}
            }
            emit("fly_finder_share_copy", { fields: Object.keys(answers || {}) });
          }
        }, ["Copy link"]);
        card.appendChild(el("div", { class: "ffinder-share" }, [
          el("label", { class: "ffinder-share__label" }, ["Share this recommendation with your barn"]),
          el("div", { class: "ffinder-share__row" }, [shareInput, shareBtn])
        ]));
      }
    }

    // Email capture — Klaviyo opt-in. Rendered ABOVE the product grid so
    // customers see "Email me my results" before they start clicking through
    // to PDPs (Den 2026-05-13 finding #1: discoverability). Hidden when no
    // public key or in profile-review mode (already captured originally).
    // If the customer already submitted at step 15, swap the form for a
    // compact "Sent — check your inbox" confirmation so we don't show a
    // duplicate form (Caitlin 2026-05-26).
    if (!isProfileReview && self.opts.enableEmailCapture && self.opts.klaviyoPublicKey) {
      if (self.state.emailCapturedAtStep) {
        card.appendChild(this.renderEmailCaptureSent());
      } else {
        card.appendChild(this.renderEmailCapture(completePayload));
      }
    }

    card.appendChild(grid);
    card.appendChild(actions);
    this.root.appendChild(card);

    if (typeof this.opts.onComplete === "function") {
      try { this.opts.onComplete(resolved); } catch (e) {}
    }
  };

  // Compact confirmation block shown on the result page when the customer
  // already submitted their email at step 15. Keeps the same visual real
  // estate the form occupies so the page layout doesn't shift.
  FlyFitFinder.prototype.renderEmailCaptureSent = function () {
    return el("div", { class: "ffinder-email ffinder-email--confirm" }, [
      el("h3", { class: "ffinder-email__heading" }, ["\u2713 Sent — check your inbox."]),
      el("p", { class: "ffinder-email__help" }, [
        "We just emailed your personalized fly sheet recommendations. The link in that email opens this exact page so you can come back anytime."
      ])
    ]);
  };

  FlyFitFinder.prototype.renderEmailCapture = function (completePayload) {
    var self = this;
    var wrap = el("div", { class: "ffinder-email" });
    var heading = el("h3", { class: "ffinder-email__heading" }, ["Email me these recommendations"]);
    var help = el("p", { class: "ffinder-email__help" }, ["We'll send a link to this exact result so you can review it later or forward it to your barn."]);
    var input = el("input", {
      type: "email",
      class: "ffinder-input",
      placeholder: "you@example.com",
      autocomplete: "email",
      "aria-label": "Email address"
    });
    var optin = el("label", { class: "ffinder-email__optin" }, [
      el("input", { type: "checkbox", checked: "checked" }),
      el("span", {}, ["Also send me Schneiders seasonal fly-care tips."])
    ]);
    var status = el("div", { class: "ffinder-email__status", role: "status", "aria-live": "polite" });
    var submit = el("button", {
      type: "button",
      class: "ffinder-btn ffinder-btn--primary",
      onclick: function () {
        var email = String(input.value || "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          status.textContent = "Please enter a valid email address.";
          return;
        }
        var optedIn = optin.querySelector("input[type=checkbox]").checked;
        self.state.answers.email = email;
        klaviyoIdentify(self.opts, {
          $email: email,
          horse_name: self.state.answers.horse_name || null,
          fly_finder_last_size: completePayload.fly_size || null,
          fly_finder_last_fit: completePayload.fly_fit || null,
          fly_finder_last_fabric: completePayload.fly_fabric || null,
          fly_finder_marketing_optin: !!optedIn
        });
        klaviyoTrack(self.opts, "Fly Finder Email Capture", Object.assign({
          email: email,
          marketing_optin: !!optedIn,
          share_url: buildShareUrl(self.opts, self.state.answers)
        }, completePayload));
        if (optedIn) klaviyoSubscribe(self.opts, email);
        emit("fly_finder_email_submit", { marketing_optin: !!optedIn });
        status.textContent = "Sent! Check your inbox in a minute or two.";
        submit.disabled = true;
        input.disabled = true;
      }
    }, ["Email me my results"]);
    wrap.appendChild(heading);
    wrap.appendChild(help);
    wrap.appendChild(el("div", { class: "ffinder-email__row" }, [input, submit]));
    wrap.appendChild(optin);
    wrap.appendChild(status);
    return wrap;
  };

  // --------------------------------------------------------------------------
  // Public surface
  // --------------------------------------------------------------------------
  root.FlyFitFinder = {
    mount: function (rootEl, opts) {
      var instance = new FlyFitFinder(rootEl, opts);
      instance.mount();
      return instance;
    },
    /** Exposed for unit tests / network mirror; resolves answers without UI. */
    resolve: function (answers, rules) { return resolve(answers, rules); },
    expandColumnar: expandColumnar,
    STEPS: STEPS,
    HEIGHT_OPTIONS_BY_TYPE: HEIGHT_OPTIONS_BY_TYPE,
    NARRATIVE: NARRATIVE,
    narrate: narrate,
    ageNarrative: ageNarrative,
    buildHorseSummary: buildHorseSummary,
    buildCompletePayload: buildCompletePayload,
    resolveSlot: resolveSlot,
    RESULT_SLOTS: RESULT_SLOTS,
    breedTypeWarning: breedTypeWarning,
    encodeAnswers: encodeAnswers,
    decodeAnswers: decodeAnswers,
    buildShareUrl: function (opts, answers) { return buildShareUrl(Object.assign({}, DEFAULTS, opts || {}), answers); },
    klaviyoSubscribe: klaviyoSubscribe,
    findClosestSizeRow: findClosestSizeRow
  };
})(typeof window !== "undefined" ? window : globalThis);
